/**
 * Table Operations Constants
 *
 * Defines operation types, statuses, and error types for table operations.
 * Used for the table operations journal that tracks all table mutations.
 */

// ============================================================================
// OPERATION TYPES
// ============================================================================

/**
 * Types of operations that can be performed on tables.
 * Each operation is tracked in the table_operations journal.
 */
export const TABLE_OPERATION_TYPES = [
  'type_conversion', // Changing a field's data type (triggers Parquet rewrite)
  'constraint_change', // Changing field constraints (unique/required) - validation only
  'field_added', // Adding a new field to the table
  'field_removed', // Removing a field from the table
  'field_renamed', // Renaming a field
  'field_reordered', // Changing field display order
  'field_metadata_updated', // Updating field metadata (description)
  'version_uploaded', // New version uploaded and processed
  'version_deleted', // Version was deleted
  'table_finalized', // Draft table was finalized (made active)
] as const;

export type TableOperationType = (typeof TABLE_OPERATION_TYPES)[number];

/**
 * Display labels for operation types
 */
export const TABLE_OPERATION_TYPE_LABELS: Record<TableOperationType, string> = {
  type_conversion: 'Type Conversion',
  constraint_change: 'Constraint Change',
  field_added: 'Field Added',
  field_removed: 'Field Removed',
  field_renamed: 'Field Renamed',
  field_reordered: 'Field Reordered',
  field_metadata_updated: 'Field Metadata Updated',
  version_uploaded: 'Version Uploaded',
  version_deleted: 'Version Deleted',
  table_finalized: 'Table Finalized',
} as const;

// ============================================================================
// OPERATION STATUSES
// ============================================================================

/**
 * Status of a table operation.
 * Operations progress through: pending → in_progress → completed/failed
 */
export const TABLE_OPERATION_STATUSES = [
  'pending', // Operation queued, not yet started
  'in_progress', // Operation is currently running
  'completed', // Operation finished successfully
  'failed', // Operation encountered an error
] as const;

export type TableOperationStatus = (typeof TABLE_OPERATION_STATUSES)[number];

/**
 * Display labels for operation statuses
 */
export const TABLE_OPERATION_STATUS_LABELS: Record<TableOperationStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  failed: 'Failed',
} as const;

// ============================================================================
// OPERATION EVENT LEVELS
// ============================================================================

/**
 * Levels for operation events.
 * Determines the type and severity of the event.
 */
export const OPERATION_EVENT_LEVELS = [
  'info', // Informational progress message
  'warning', // Non-critical warning
  'success', // Successful completion of a step
  'error', // General error during processing
  'validation_error', // Field-level validation failure with structured context
  'coercion_warning', // Type coercion was applied (e.g., date string -> epoch for number field)
] as const;

export type OperationEventLevel = (typeof OPERATION_EVENT_LEVELS)[number];

// ============================================================================
// COERCION METHODS
// ============================================================================

/**
 * Methods used to coerce values during file parsing.
 * Stored in operation_events.details.coercion_method for audit trail.
 */
export const COERCION_METHODS = [
  'native', // Value was already correct type
  'string_to_number', // Parsed numeric string
  'currency_to_number', // Stripped currency symbols
  'percentage_to_number', // Converted percentage
  'date_to_epoch', // Converted date string to epoch seconds
  'datetime_to_epoch', // Converted datetime to epoch seconds
  'boolean_to_number', // Converted true/false to 1/0
  'string_to_boolean', // Parsed boolean string
  'number_to_boolean', // Converted 0/1 to boolean
  'iso_date', // Parsed ISO date format
  'us_date', // Parsed US date format (MM/DD/YYYY)
  'eu_date', // Parsed EU date format (DD/MM/YYYY)
  'natural_date', // Parsed natural language date
  'epoch_to_date', // Converted epoch to date
  'excel_to_date', // Converted Excel serial to date
  'datetime_to_date', // Stripped time from datetime
  'date_to_datetime', // Added midnight time to date
  'epoch_to_datetime', // Converted epoch to datetime
  'excel_to_datetime', // Converted Excel serial to datetime
  'trim_whitespace', // Trimmed whitespace
  'add_protocol', // Added https:// to URL
  'json_parse', // Parsed JSON string
  'to_string', // Converted to string
  'failed', // Coercion failed
] as const;

export type CoercionMethod = (typeof COERCION_METHODS)[number];

/**
 * Coercion warning attached to a parsed record.
 * Used during file parsing to track type coercions.
 */
export interface RecordCoercionWarning {
  /** Field name that was coerced */
  fieldName: string;
  /** Original value before coercion */
  originalValue: unknown;
  /** Detected original type */
  originalType: string;
  /** Value after coercion (null if failed) */
  coercedValue: unknown;
  /** Method used for coercion */
  coercionMethod: CoercionMethod;
  /** Human-readable message */
  message: string;
}

/**
 * Details stored in operation_events.details for coercion_warning level.
 * Matches the structure used in ValidationErrorPersister.persistCoercionWarnings().
 */
export interface CoercionWarningEventDetails {
  /** Original value as string */
  original_value: string;
  /** Detected original type */
  original_type: string;
  /** Coerced value as string (null if failed) */
  coerced_value: string | null;
  /** Method used for coercion */
  coercion_method: CoercionMethod;
}

// ============================================================================
// CONVERSION ERROR TYPES
// ============================================================================

/**
 * Types of errors that can occur during type conversion.
 * Stored in operation_events with the error_code field.
 */
export const CONVERSION_ERROR_TYPES = [
  'parse_error', // Value could not be parsed as target type (e.g., "abc" → number)
  'overflow', // Value exceeds target type limits (e.g., huge number → int32)
  'format_mismatch', // Value format doesn't match expected (e.g., "2024-13-45" → date)
  'null_violation', // Null value when field is required
  'type_mismatch', // Incompatible type conversion requested
] as const;

export type ConversionErrorType = (typeof CONVERSION_ERROR_TYPES)[number];

/**
 * Display labels for conversion error types
 */
export const CONVERSION_ERROR_TYPE_LABELS: Record<ConversionErrorType, string> = {
  parse_error: 'Parse Error',
  overflow: 'Value Overflow',
  format_mismatch: 'Format Mismatch',
  null_violation: 'Null Violation',
  type_mismatch: 'Type Mismatch',
} as const;

// ============================================================================
// TYPE CONVERSION PARAMS
// ============================================================================

/**
 * Parameters for a type conversion operation.
 * Stored in table_operations.params as JSONB.
 */
export interface TypeConversionOperationParams {
  /** ID of the field being converted */
  field_id: string;
  /** Original data type before conversion */
  from_type: string;
  /** Target data type after conversion */
  to_type: string;
  /** Optional format hint for parsing (e.g., date format string) */
  format_hint?: string;
}

/**
 * Result of a type conversion operation.
 * Stored in table_operations.result as JSONB.
 */
export interface TypeConversionOperationResult {
  /** Number of rows successfully converted */
  rows_converted: number;
  /** Number of rows with conversion errors */
  rows_failed: number;
  /** List of version IDs that were rewritten */
  versions_processed: string[];
  /** Total time in milliseconds */
  duration_ms: number;
}

/**
 * A single field conversion in a batch operation.
 */
export interface BatchFieldConversion {
  /** ID of the field being converted */
  field_id: string;
  /** Name of the field (for display) */
  field_name: string;
  /** Original data type before conversion */
  from_type: string;
  /** Target data type after conversion */
  to_type: string;
}

/**
 * Parameters for a batch type conversion operation.
 * Stored in table_operations.params as JSONB.
 */
export interface BatchTypeConversionOperationParams {
  /** Array of field conversions to perform */
  field_conversions: BatchFieldConversion[];
}

/**
 * Per-field result in batch conversion.
 */
export interface BatchFieldConversionResult {
  /** Field ID */
  field_id: string;
  /** Field name */
  field_name: string;
  /** Whether validation passed for this field */
  valid: boolean;
  /** Number of values that failed conversion */
  failed_count: number;
}

/**
 * Result of a batch type conversion operation.
 * Stored in table_operations.result as JSONB.
 */
export interface BatchTypeConversionOperationResult {
  /** Number of rows successfully converted */
  rows_converted: number;
  /** Number of rows with conversion errors */
  rows_failed: number;
  /** List of version IDs that were rewritten */
  versions_processed: string[];
  /** Per-field results */
  field_results: BatchFieldConversionResult[];
  /** Total time in milliseconds */
  duration_ms: number;
}

// ============================================================================
// CONSTRAINT CHANGE PARAMS
// ============================================================================

/**
 * Type of constraint that can be changed
 */
export const CONSTRAINT_TYPES = ['unique', 'required'] as const;
export type ConstraintType = (typeof CONSTRAINT_TYPES)[number];

/**
 * Parameters for a constraint change operation.
 * Stored in table_operations.params as JSONB.
 */
export interface ConstraintChangeOperationParams {
  /** ID of the field being modified */
  field_id: string;
  /** Name of the field (for display) */
  field_name: string;
  /** Type of constraint being changed */
  constraint_type: ConstraintType;
  /** Original constraint value */
  from_value: boolean;
  /** Target constraint value */
  to_value: boolean;
}

/**
 * A single constraint violation found during validation
 */
export interface ConstraintViolation {
  /** Version where violation was found */
  version_id: string;
  /** Row number (1-indexed) */
  row_number: number;
  /** The problematic value (null for required, duplicate for unique) */
  value: unknown;
  /** Human-readable reason */
  reason: string;
}

/**
 * Result of a constraint change operation.
 * Stored in table_operations.result as JSONB.
 */
export interface ConstraintChangeOperationResult {
  /** Whether validation passed (constraint was applied) */
  valid: boolean;
  /** Total rows checked across all versions */
  rows_checked: number;
  /** Number of rows that violated the constraint */
  violations_found: number;
  /** Sample violations (up to 100) for user review */
  sample_violations?: ConstraintViolation[];
  /** List of version IDs that were validated */
  versions_checked: string[];
  /** Total time in milliseconds */
  duration_ms: number;
}

// ============================================================================
// VERSION UPLOADED PARAMS
// ============================================================================

/**
 * Parameters for a version upload operation.
 * Stored in table_operations.params as JSONB.
 */
export interface VersionUploadedOperationParams {
  /** ID of the version being processed */
  version_id: string;
  /** Original file name */
  file_name: string;
  /** File size in bytes */
  file_size: number;
  /** Optional sheet name for Excel files */
  sheet_name?: string;
}

/**
 * Result of a version upload operation.
 * Stored in table_operations.result as JSONB.
 */
export interface VersionUploadedOperationResult {
  /** Number of records successfully processed */
  records_processed: number;
  /** Total records in file */
  records_total: number;
  /** Parquet file path in R2 */
  parquet_path: string;
  /** Parquet file size in bytes */
  parquet_size: number;
  /** Processing duration in milliseconds */
  duration_ms: number;
}

// ============================================================================
// FIELD METADATA UPDATED PARAMS
// ============================================================================

/**
 * A single change to a field's metadata property
 */
export interface FieldMetadataChange {
  from: unknown;
  to: unknown;
}

/**
 * A single field's metadata changes
 */
export interface FieldMetadataUpdateItem {
  /** ID of the field */
  fieldId: string;
  /** Name of the field (for display) */
  fieldName: string;
  /** Changes made to the field's metadata */
  changes: Record<string, FieldMetadataChange>;
}

/**
 * Parameters for a field metadata update operation.
 * Stored in table_operations.params as JSONB.
 */
export interface FieldMetadataUpdatedOperationParams {
  fields: FieldMetadataUpdateItem[];
}

// ============================================================================
// FIELD RENAMED PARAMS
// ============================================================================

/**
 * A single field rename change
 */
export interface FieldRenameItem {
  /** ID of the field */
  fieldId: string;
  /** Original field name */
  fromName: string;
  /** New field name */
  toName: string;
}

/**
 * Parameters for a field renamed operation.
 * Stored in table_operations.params as JSONB.
 */
export interface FieldRenamedOperationParams {
  fields: FieldRenameItem[];
}

// ============================================================================
// FIELD REORDERED PARAMS
// ============================================================================

/**
 * A single field reorder change
 */
export interface FieldReorderItem {
  /** ID of the field */
  fieldId: string;
  /** Name of the field (for display) */
  fieldName: string;
  /** Original order index */
  fromIndex: number;
  /** New order index */
  toIndex: number;
}

/**
 * Parameters for a field reordered operation.
 * Stored in table_operations.params as JSONB.
 */
export interface FieldReorderedOperationParams {
  fields: FieldReorderItem[];
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if a status indicates the operation is still active (not terminal)
 */
export function isActiveOperationStatus(status: TableOperationStatus): boolean {
  return status === 'pending' || status === 'in_progress';
}

/**
 * Check if a status indicates the operation is terminal (completed or failed)
 */
export function isTerminalOperationStatus(status: TableOperationStatus): boolean {
  return status === 'completed' || status === 'failed';
}

/**
 * Format operation params into a human-readable summary string.
 * Single source of truth for operation summary formatting.
 */
export function formatOperationSummary(
  operationType: TableOperationType,
  params: unknown
): string {
  if (!params) return '-';

  switch (operationType) {
    case 'type_conversion': {
      const p = params as BatchTypeConversionOperationParams;
      if (p.field_conversions && p.field_conversions.length > 0) {
        const fc = p.field_conversions[0];
        if (p.field_conversions.length === 1) {
          return `${fc.field_name}: ${fc.from_type} → ${fc.to_type}`;
        }
        return `${p.field_conversions.length} fields`;
      }
      return '-';
    }
    case 'constraint_change': {
      const p = params as ConstraintChangeOperationParams;
      const action = p.to_value ? 'Enable' : 'Disable';
      const fieldName = p.field_name || 'field';
      return `${fieldName}: ${action} ${p.constraint_type}`;
    }
    case 'version_uploaded': {
      const p = params as VersionUploadedOperationParams;
      return p.file_name || 'File uploaded';
    }
    case 'field_renamed': {
      const p = params as FieldRenamedOperationParams;
      if (p.fields && p.fields.length > 0) {
        const summaries = p.fields.map((f) => `${f.fromName} → ${f.toName}`);
        return summaries.join(', ');
      }
      return '-';
    }
    case 'field_metadata_updated': {
      const p = params as FieldMetadataUpdatedOperationParams;
      if (p.fields && p.fields.length > 0) {
        const summaries = p.fields.map((f) => {
          const changeKeys = Object.keys(f.changes);
          return `${f.fieldName}: ${changeKeys.join(', ')}`;
        });
        return summaries.join('; ');
      }
      return '-';
    }
    case 'field_reordered': {
      const p = params as FieldReorderedOperationParams;
      if (p.fields && p.fields.length > 0) {
        const fieldNames = p.fields.map((f) => f.fieldName);
        if (fieldNames.length === 1) {
          return `${fieldNames[0]}: ${p.fields[0].fromIndex + 1} → ${p.fields[0].toIndex + 1}`;
        }
        return `${fieldNames.length} fields reordered`;
      }
      return '-';
    }
    case 'field_added': {
      const p = params as { field_name?: string; data_type?: string };
      if (p.field_name) {
        return p.data_type ? `${p.field_name} (${p.data_type})` : p.field_name;
      }
      return '-';
    }
    case 'field_removed':
    case 'version_deleted':
    case 'table_finalized':
      // These can be extended as needed
      return '-';
  }
}
